import React, { useState, useEffect, useCallback } from "react";
import { Dropdown, Form, Button, Spinner, Badge } from "react-bootstrap";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { MdSportsMotorsports, MdPerson, MdHome } from "react-icons/md";
import io from "socket.io-client";
import { getUserFromLocalStorage } from "../../../../utilities/user";
import { getVendorRidersApi } from "../../../../api";
import socketAuthOptions from "../../../../utilities/socketAuth";
import { attachVendorOrderRooms } from "../../../../hooks/useVendorOrderRooms";
import { getOrderVendorId } from "../../../../utilities/orderPresentation";
import { riderLockState, riderLockMessage } from "../../../../utilities/orderRiderLock";
import { useGate } from "../../../../components/team/Gated";

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    style={{
      padding: "10px 14px",
      backgroundColor: "#2e2e4a",
      border: "1px solid #3a3a58",
      borderRadius: "5px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: "#e0e0ff",
      fontSize: "12px",
      minHeight: "34px",
      width: "100%",
    }}
  >
    <span>{children}</span>
    <span style={{ marginLeft: "10px" }}>&#x25bc;</span>
  </div>
));

// Reachability dot shown next to online in-house riders
const ReachabilityDot = ({ lastActiveAt }) => {
  const minutesAgo = lastActiveAt
    ? Math.floor((Date.now() - new Date(lastActiveAt).getTime()) / 60000)
    : null;
  // Green within the connected window, amber within the reachable window,
  // red beyond it (the same bands as utils/riderPresence.js on the server).
  const color = minutesAgo === null ? "#9ca3af"
    : minutesAgo <= 2 ? "#01cf4d"
    : minutesAgo <= 10 ? "#facc15"
    : "#e63757";
  const label = minutesAgo === null ? "no heartbeat"
    : minutesAgo === 0 ? "Just now"
    : minutesAgo < 60 ? `${minutesAgo}m ago`
    : minutesAgo < 1440 ? `${Math.floor(minutesAgo / 60)}h ago`
    : `${Math.floor(minutesAgo / 1440)}d ago`;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 4 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: color, display: "inline-block", flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>{label}</span>
    </span>
  );
};

const RiderPicker = ({
  order,
  riderId,
  setRiderId,
  loadingRiders,
  riders,
  assignRestaurantOrderToRider,
  elapsedAssignedTime,
  elapsedDispatchedTime,
  onRiderSelect,
}) => {
  const user = getUserFromLocalStorage();
  const accountVendorId = user?.result?.parentVendorId || user?.result?._id;
  const vendorId = getOrderVendorId(order, accountVendorId);
  // Vendor team permissions: assigning a rider is orders:manage.
  const ordersGate = useGate("orders:manage");

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRiders, setFilteredRiders] = useState(riders);
  const [localRiders, setLocalRiders] = useState(riders);
  const [inHouseRiders, setInHouseRiders] = useState([]);
  const [loadingInHouse, setLoadingInHouse] = useState(false);
  const [, setTick] = useState(0);

  const selectedRider = localRiders?.find((r) => r._id === riderId)
    || inHouseRiders?.find((r) => r._id === riderId);

  // Tick every 60s so reachability dots update live
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch vendor's own in-house riders with reachability
  useEffect(() => {
    if (!vendorId) return undefined;
    let active = true;
    setLoadingInHouse(true);
    getVendorRidersApi(vendorId)
      .then((res) => {
        if (!active) return;
        const riderList = res.data?.data?.riders || res.data || [];
        setInHouseRiders(Array.isArray(riderList) ? riderList : []);
      })
      .catch(() => {
        if (!active) return;
        // Silently fail — in-house section simply won't appear
        setInHouseRiders([]);
      })
      .finally(() => {
        if (active) setLoadingInHouse(false);
      });
    return () => { active = false; };
  }, [vendorId]);

  const updateRiderStatus = useCallback((updatedRiderId, status) => {
    setLocalRiders((prev) =>
      prev.map((r) => r._id === updatedRiderId ? { ...r, onlineStatus: status } : r)
    );
    setInHouseRiders((prev) =>
      prev.map((r) => r._id === updatedRiderId ? { ...r, onlineStatus: status } : r)
    );
  }, []);

  const updateHeartbeat = useCallback(({ riderId: rId, lastActiveAt }) => {
    setInHouseRiders((prev) =>
      prev.map((r) => r._id === rId ? { ...r, lastActiveAt } : r)
    );
  }, []);

  useEffect(() => {
    if (!vendorId) return undefined;
    const socket = io(
      process.env.REACT_APP_SERVER_URL,
      socketAuthOptions({ userId: user?.result?._id })
    );

    const onRiderStatusUpdate = ({ riderId: rId, status }) => {
      updateRiderStatus(rId, status);
    };
    // Carries the server's presence verdict (utils/riderPresence.js on the
    // backend): status is what the rider IS, reachability says why.
    const onRiderStatusChanged = ({ riderId: rId, status, isAvailable, reachability, lastActiveAt }) => {
      setInHouseRiders((prev) =>
        prev.map((r) => r._id === rId
          ? {
              ...r,
              onlineStatus: status,
              availability: { ...r.availability, isAvailable },
              ...(reachability ? { reachability } : {}),
              ...(lastActiveAt ? { lastActiveAt } : {}),
            }
          : r)
      );
    };
    socket.on("riderStatusUpdate", onRiderStatusUpdate);
    socket.on("rider:heartbeatUpdate", updateHeartbeat);
    socket.on("rider:statusChanged", onRiderStatusChanged);

    // One join, on connect and every reconnect (4 Oct 2026): the server picks
    // the whole-business rooms for head office or the branch rooms for a
    // member limited to some branches, where rider presence also arrives. A
    // server older than this page gets the old join.
    const stopRooms = attachVendorOrderRooms(socket, vendorId, {
      fallback: (s) => s.emit("joinVendorRoom", vendorId),
    });

    return () => {
      stopRooms();
      socket.off("riderStatusUpdate", onRiderStatusUpdate);
      socket.off("rider:heartbeatUpdate", updateHeartbeat);
      socket.off("rider:statusChanged", onRiderStatusChanged);
      // Disconnecting leaves every room this private socket joined.
      socket.disconnect();
    };
  }, [updateRiderStatus, updateHeartbeat, user?.result?._id, vendorId]);

  // IDs of in-house riders so they don't duplicate in the global list
  const inHouseIds = new Set(inHouseRiders.map((r) => r._id));

  const globalRiders = localRiders?.filter((r) => !inHouseIds.has(r._id));

  const searchMatch = (rider) =>
    `${rider.firstName} ${rider.lastName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

  // Surface assignable riders first: Online before Offline, un-flagged before
  // COD-limited, then alphabetical — so the one available rider is never buried
  // under offline ones.
  const sortByAvailability = (list = []) =>
    [...list].sort((a, b) => {
      const onA = a.onlineStatus === "Online" ? 0 : 1;
      const onB = b.onlineStatus === "Online" ? 0 : 1;
      if (onA !== onB) return onA - onB;
      const flagA = a.codCash?.isFlagged ? 1 : 0;
      const flagB = b.codCash?.isFlagged ? 1 : 0;
      if (flagA !== flagB) return flagA - flagB;
      return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
    });

  const onlineCount = (list = []) => list.filter((r) => r.onlineStatus === "Online").length;

  const filteredInHouse = sortByAvailability(inHouseRiders.filter(searchMatch));
  const filteredGlobal = sortByAvailability(globalRiders?.filter(searchMatch) || []);

  useEffect(() => {
    const filtered = localRiders?.filter((rider) =>
      `${rider.firstName} ${rider.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredRiders(filtered);
  }, [searchTerm, localRiders]);

  useEffect(() => {
    setLocalRiders(riders);
  }, [riders]);

  const renderRiderStatus = (rider, showReachability = true) => {
    if (!rider) return null;
    // onlineStatus is the server's verdict: the toggle while the app is
    // reachable, Offline otherwise. A rider who toggled on but whose phone
    // has gone quiet is named as unreachable, never shown as online.
    const isOnline = rider.onlineStatus === "Online";
    const unreachable = !isOnline && ["unreachable", "disconnected", "abandoned"].includes(rider.reachability);
    const statusLabel = isOnline ? "Online" : unreachable ? "Unreachable" : (rider.onlineStatus || "Offline");
    const isCodFlagged = rider.codCash?.isFlagged === true;
    // Dim offline (and further-dim COD-limited) rows so online riders pop.
    const rowOpacity = isCodFlagged ? 0.55 : isOnline ? 1 : 0.72;
    return (
      <span style={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 6, opacity: rowOpacity, width: "100%" }}>
        <FiberManualRecordIcon
          style={{
            color: isOnline ? "#4CAF50" : "#F44336",
            fontSize: "14px",
            flexShrink: 0,
            marginRight: 8
          }}
        />
        <span style={{ fontWeight: 600, fontSize: "13px", whiteSpace: "nowrap" }}>
          {[rider.firstName, rider.lastName].filter(Boolean).join(" ") || rider.name || "Rider"}
        </span>
        <span style={{ color: isOnline ? "#22a55b" : unreachable ? "#b76e00" : "#9aa0b4", fontSize: "12px", fontWeight: isOnline ? 600 : 400, whiteSpace: "nowrap" }}>
          ({statusLabel})
        </span>
        {isCodFlagged && (
          <Badge bg="danger" style={{ fontSize: "10px", marginLeft: 4, padding: "2px 5px" }}>
            COD Limit
          </Badge>
        )}
        {showReachability && (isOnline || unreachable) && !isCodFlagged && (
          <ReachabilityDot lastActiveAt={rider.lastActiveAt} />
        )}
      </span>
    );
  };

  const getAssignedRider = () => {
    if (!order?.riderId) return null;
    const populated = typeof order.riderId === "object" ? order.riderId : null;
    const id = populated ? populated._id : order.riderId;
    // The two lists are this vendor's in-house riders and the socket roster.
    // A 3PL partner's rider is in neither, so an order delivered by one showed
    // an empty "Assigned Rider" card. The order itself arrives with the rider
    // populated (GET /weborders/:orderId), so fall back to that record; the
    // lists win when present because they carry live reachability.
    return (
      inHouseRiders.find((r) => r._id === id) ||
      localRiders.find((r) => r._id === id) ||
      (populated && (populated.firstName || populated.lastName || populated.name) ? populated : null)
    );
  };

  const handleSelect = (id) => {
    const selectedRider =
      inHouseRiders.find((r) => r._id === id) ||
      localRiders.find((r) => r._id === id) ||
      null;
    setRiderId(id);
    setSearchTerm("");
    if (typeof onRiderSelect === "function") onRiderSelect(id, selectedRider);
  };

  const hasInHouse = inHouseRiders.length > 0;
  const showSections = hasInHouse;

  return (
    <Form.Group
      controlId="formRiderSelect"
      className="p-0"
      style={{
        background: "transparent",
        borderRadius: "6px",
      }}
    >
      <Form.Label style={{ fontWeight: 700, color: "#8888aa", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.7px", marginBottom: "6px" }}>
        Select Rider
      </Form.Label>
      <Dropdown className="mb-3">
        <Dropdown.Toggle
          as={CustomToggle}
          id="dropdown-custom-components"
          disabled={!!order?.riderId || !ordersGate.allowed}
          title={ordersGate.title}
        >
          {order?.riderId ? (
            renderRiderStatus(getAssignedRider(), true)
          ) : selectedRider ? (
            renderRiderStatus(selectedRider, inHouseIds.has(selectedRider._id))
          ) : (
            <span style={{ color: "#d6d8d9" }}>
              <MdPerson style={{ marginRight: "5px" }} /> Select Rider
            </span>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu
          style={{
            width: "100%",
            minWidth: "320px",
            maxHeight: "480px",
            overflowY: "auto",
            padding: "10px",
          }}
        >
          <Form.Control
            type="text"
            placeholder="Search riders..."
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            style={{ marginBottom: "10px" }}
          />

          {(loadingRiders || loadingInHouse) ? (
            <Dropdown.Item disabled>Searching for riders...</Dropdown.Item>
          ) : showSections ? (
            <>
              {/* In-house riders section */}
              <div style={{ padding: "4px 8px 2px", fontSize: "10px", fontWeight: 700, color: "#8888aa", textTransform: "uppercase", letterSpacing: "0.07em", display: "flex", alignItems: "center", gap: 6 }}>
                <MdHome style={{ fontSize: 13 }} /> My Riders
                <Badge bg="" style={{ backgroundColor: "#023485", color: "#fff", fontSize: "10px" }}>
                  {filteredInHouse.length}
                </Badge>
                {onlineCount(filteredInHouse) > 0 && (
                  <span style={{ color: "#22a55b", fontWeight: 600, letterSpacing: "normal", textTransform: "none" }}>
                    {onlineCount(filteredInHouse)} online
                  </span>
                )}
              </div>
              {filteredInHouse.length > 0 ? (
                filteredInHouse.map((rider) => (
                  <Dropdown.Item
                    key={rider._id}
                    onClick={() => handleSelect(rider._id)}
                    active={rider._id === riderId}
                    style={{ display: "flex", alignItems: "center", padding: "16px 18px", minHeight: 56 }}
                  >
                    {renderRiderStatus(rider, true)}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled style={{ fontSize: "12px", color: "#9ca3af" }}>No in-house riders match</Dropdown.Item>
              )}

              {/* Global / Lusuku pool section */}
              {filteredGlobal.length > 0 && (
                <>
                  <Dropdown.Divider />
                  <div style={{ padding: "4px 8px 2px", fontSize: "10px", fontWeight: 700, color: "#8888aa", textTransform: "uppercase", letterSpacing: "0.07em", display: "flex", alignItems: "center", gap: 6 }}>
                    Lusuku Pool
                    <Badge bg="" style={{ backgroundColor: "#5b21b6", color: "#fff", fontSize: "10px" }}>
                      {filteredGlobal.length}
                    </Badge>
                    {onlineCount(filteredGlobal) > 0 && (
                      <span style={{ color: "#22a55b", fontWeight: 600, letterSpacing: "normal", textTransform: "none" }}>
                        {onlineCount(filteredGlobal)} online
                      </span>
                    )}
                  </div>
                  {filteredGlobal.map((rider) => (
                    <Dropdown.Item
                      key={rider._id}
                      onClick={() => handleSelect(rider._id)}
                      active={rider._id === riderId}
                      style={{ display: "flex", alignItems: "center", padding: "12px 14px", minHeight: 48 }}
                    >
                      {renderRiderStatus(rider, true)}
                    </Dropdown.Item>
                  ))}
                </>
              )}
            </>
          ) : filteredRiders?.length > 0 ? (
            sortByAvailability(filteredRiders).map((rider) => (
              <Dropdown.Item
                key={rider._id}
                onClick={() => handleSelect(rider._id)}
                active={rider._id === riderId}
                style={{ display: "flex", alignItems: "center", padding: "12px 14px", minHeight: 48 }}
              >
                {renderRiderStatus(rider)}
              </Dropdown.Item>
            ))
          ) : (
            <Dropdown.Item disabled>No riders found</Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {loadingRiders && (
        <div style={{ marginLeft: "2px" }}>
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {order?.riderId && (
        <div
          style={{
            marginTop: "12px",
            fontSize: "13px",
            backgroundColor: "#2a2a40",
            border: "1px solid #3a3a58",
            padding: "12px",
            borderRadius: "6px",
            color: "#ccccee",
          }}
        >
          <p style={{ fontWeight: 700, marginBottom: "5px", color: "#8888aa", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.6px" }}>
            Assigned Rider:
          </p>
          <p style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
            {renderRiderStatus(getAssignedRider(), true)}
            <MdSportsMotorsports style={{ marginLeft: "10px", fontSize: "19px", color: "green" }} />
          </p>
          {order?.delivery_status === "ASSIGNED" && (
            <p style={{ fontSize: "12px", color: "#8888aa", marginBottom: "0" }}>
              Time Elapsed after Assigning:{" "}
              {Math.floor(elapsedAssignedTime / 3600)}h{" "}
              {Math.floor((elapsedAssignedTime % 3600) / 60)}m{" "}
              {elapsedAssignedTime % 60}s
            </p>
          )}
          {order?.delivery_status === "DISPATCHED" && (
            <p style={{ fontSize: "12px", color: "#8888aa", marginBottom: "0" }}>
              Time Elapsed from Dispatch:{" "}
              {Math.floor(elapsedDispatchedTime / 3600)}h{" "}
              {Math.floor((elapsedDispatchedTime % 3600) / 60)}m{" "}
              {elapsedDispatchedTime % 60}s
            </p>
          )}
        </div>
      )}
      {!order?.riderId && selectedRider?.codCash?.isFlagged && (
        <div style={{
          marginTop: "10px",
          padding: "8px 12px",
          backgroundColor: "rgba(251,146,60,0.1)",
          border: "1px solid rgba(251,146,60,0.35)",
          borderRadius: "6px",
          fontSize: "12px",
          color: "#fb923c",
          lineHeight: 1.5
        }}>
          ⚠️ <strong>COD Limit:</strong> This rider has UGX {(selectedRider.codCash?.heldBalance || 0).toLocaleString()} in held cash.
          Assigning anyway will override the limit.
        </div>
      )}
      {!order?.riderId && (
        <Button
          variant={selectedRider?.codCash?.isFlagged ? "warning" : "primary"}
          onClick={assignRestaurantOrderToRider}
          className="mt-3"
          disabled={!riderId || loadingRiders || !ordersGate.allowed}
          title={ordersGate.title}
          style={{
            visibility: riderId ? "visible" : "hidden",
            width: "100%",
            padding: "10px",
            fontWeight: "bold",
          }}
        >
          {selectedRider?.codCash?.isFlagged ? '⚠️ Override & Assign' : 'Assign Order to Rider'}
        </Button>
      )}
    </Form.Group>
  );
};

/**
 * Read-only rider card for a finished order.
 *
 * A delivered order used to keep the full picker (and, when its rider could
 * not be resolved, an enabled "Select Rider" box with an Assign button). The
 * server now refuses those writes (409 ORDER_NOT_ASSIGNABLE, see
 * utils/orderRiderLock.js); this card stops offering them. It is its own
 * component so a finished order never opens the rider socket or fetches the
 * vendor's rider roster just to show history.
 */
const RiderAssignmentClosed = ({ order, state }) => {
  const populated = order?.riderId && typeof order.riderId === "object" ? order.riderId : null;
  const riderName = populated
    ? [populated.firstName, populated.lastName].filter(Boolean).join(" ") || populated.name
    : null;
  const hadRider = Boolean(order?.riderId);
  const isComplete = ["DELIVERED", "FULFILLED", "NOT_APPLICABLE"].includes(state);
  return (
    <div
      role="status"
      style={{
        fontSize: "13px",
        backgroundColor: "#2a2a40",
        border: "1px solid #3a3a58",
        padding: "14px",
        borderRadius: "6px",
        color: "#ccccee",
      }}
    >
      <p style={{ fontWeight: 700, marginBottom: "6px", color: "#8888aa", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.6px" }}>
        {isComplete ? "Delivered by" : "Rider"}
      </p>
      <p style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "8px", fontWeight: 600, color: "#e0e0ff" }}>
        <MdSportsMotorsports style={{ fontSize: "19px", color: hadRider ? "#4CAF50" : "#8888aa" }} />
        {riderName || (hadRider ? "Rider on record" : "No rider was recorded")}
      </p>
      <p style={{ fontSize: "12px", color: "#9aa0b4", marginBottom: 0, lineHeight: 1.5 }}>
        {riderLockMessage(state)}
      </p>
    </div>
  );
};

const RiderSelectionDropdown = (props) => {
  const state = riderLockState(props.order);
  if (state) return <RiderAssignmentClosed order={props.order} state={state} />;
  return <RiderPicker {...props} />;
};

export default RiderSelectionDropdown;
